import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import { MenuBar } from "../../components/Menu/Menu";

const PrivacyPolicy = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();

  const closeMenuBar = () => {
    setMenuOpen(false);
  };

  const openMenuBar = () => {
    setMenuOpen(true);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col justify-between">
      <div>
        <div className="max-w-screen-2xl mx-auto px-4">
          <Header openMenu={openMenuBar} />
        </div>

        <main className="max-w-4xl mx-auto px-6 py-10">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-[var(--muted)]">
            <Link to="/" className="hover:text-[var(--brand)] transition-colors">
              {t("footer.home") || "Home"}
            </Link>
            <span>/</span>
            <span className="text-[var(--text)] font-medium">
              {t("footer.privacy") || "Privacy Policy"}
            </span>
          </nav>

          {/* Header Card */}
          <div className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm mb-8">
            <div className="inline-flex items-center gap-2 bg-[rgba(108,99,255,.1)] text-[var(--brand)] text-xs font-semibold px-3 py-1 rounded-full mb-3">
              501(c)(3) Non-Profit Organization · EIN 99-3850314
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-[var(--text)] mb-2">
              Privacy Policy
            </h1>
            <p className="text-sm text-[var(--muted)] mb-4">
              Effective Date: October 10, 2026 &middot; Last Updated: October 10, 2026
            </p>
            <p className="text-gray-700 leading-relaxed text-base">
              Welcome to Lingo Club (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains what information we collect, how we use and safeguard it, and your rights when you visit or use the LingoClub platform (
              <a href="https://lingoclub.org" className="text-[var(--brand)] underline hover:text-[var(--brand-dark)]">
                lingoclub.org
              </a>
              ) and related educational services.
            </p>
          </div>

          {/* Main Content Sections */}
          <div className="space-y-6 text-gray-800">
            {/* Section 1 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">1.</span>
                Information We Collect
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect personal information that you provide to us directly when creating an account, participating in language courses, or interacting with our services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
                <li>
                  <strong className="text-[var(--text)]">Account Information:</strong> When you register as a learner or leader (tutor), we collect your name, email address, password, profile photo, and role preference.
                </li>
                <li>
                  <strong className="text-[var(--text)]">Profile &amp; Bio Information:</strong> Information about the languages you speak, languages you are learning or teaching, your proficiency level, biography, and time zone.
                </li>
                <li>
                  <strong className="text-[var(--text)]">Scheduling &amp; Meeting Data:</strong> Meeting availability schedules, session dates and times, course subjects, attendance logs, and participant notes.
                </li>
                <li>
                  <strong className="text-[var(--text)]">Volunteer Service Records:</strong> For leaders serving as volunteer tutors, we maintain records of completed volunteer hours to certify community service, including the President&apos;s Volunteer Service Award (PVSA).
                </li>
                <li>
                  <strong className="text-[var(--text)]">Payment &amp; Transaction Details:</strong> When you purchase Lingo Credits or make donations, payment transactions are processed securely through our third-party payment processor, Stripe. We do not store your complete credit card numbers or banking credentials on our servers.
                </li>
                <li>
                  <strong className="text-[var(--text)]">Technical &amp; Log Data:</strong> When you access our website, our servers automatically log standard web data such as your IP address, browser type, operating system, pages visited, and timestamps.
                </li>
              </ul>
            </section>

            {/* Section 2 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">2.</span>
                How We Use Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect for legitimate educational and non-profit purposes, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
                <li>Facilitating language practice meetings and tutoring sessions between learners and leaders.</li>
                <li>Creating and managing your user profile and account authentication.</li>
                <li>Generating and delivering video conference invitations (e.g., Zoom or VooV meeting links).</li>
                <li>Managing Lingo Credit balances and processing leader rewards or reimbursements.</li>
                <li>Validating and certifying volunteer hours and awards for eligible volunteers.</li>
                <li>Providing customer support and responding to user inquiries and feedback.</li>
                <li>Monitoring and improving the quality, safety, and functionality of our platform.</li>
                <li>Protecting against fraud, unauthorized activities, and violations of our Terms &amp; Conditions.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">3.</span>
                Information Sharing &amp; Disclosure
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do <strong className="text-gray-900">NOT</strong> sell, rent, or trade your personal information to third parties for marketing or advertising purposes. We only share information in the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
                <li>
                  <strong className="text-[var(--text)]">With Other Users:</strong> Your public profile information (such as your name, profile photo, languages, bio, and scheduling availability) is visible to other registered users to enable session booking. Your email address and private account credentials are not publicly displayed.
                </li>
                <li>
                  <strong className="text-[var(--text)]">Video Conferencing Tools:</strong> Meeting links are hosted on third-party video conferencing services (such as Zoom or VooV). Joining a meeting shares your display name and video/audio stream with session participants.
                </li>
                <li>
                  <strong className="text-[var(--text)]">Service Providers:</strong> We may share data with trusted third-party providers who perform services on our behalf, such as cloud infrastructure hosting, database management, email delivery, and payment processing (Stripe). These providers are contractually obligated to protect your data.
                </li>
                <li>
                  <strong className="text-[var(--text)]">Legal Requirements:</strong> We may disclose information if required to do so by applicable law, regulation, court order, or governmental authority, or when necessary to protect the rights, property, or safety of Lingo Club, our users, or the public.
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">4.</span>
                Data Security
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We implement industry-standard technical and organizational security measures to protect your personal data from unauthorized access, alteration, disclosure, or destruction. All data transmitted between your browser and our servers is encrypted using HTTPS / TLS encryption. User passwords are encrypted and hashed before being stored. However, please understand that no internet transmission or electronic storage method can be guaranteed 100% secure.
              </p>
            </section>

            {/* Section 5 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">5.</span>
                Data Retention
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as your account remains active or as needed to provide you with our services, fulfill volunteer service certifications, resolve disputes, and comply with legal requirements. If you choose to close your account, we will delete or anonymize your personal information upon request, subject to necessary legal and auditing retention policies.
              </p>
            </section>

            {/* Section 6 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">6.</span>
                Children&apos;s Privacy (COPPA Compliance)
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                As a non-profit educational platform, Lingo Club is dedicated to maintaining a safe learning environment.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We do not knowingly collect personal information directly from children under 13 without verifiable consent from a parent or legal guardian. If you are a parent or guardian and discover that your child under 13 has provided personal information without consent, please contact us immediately at{" "}
                <a href="mailto:support@lingoclub.org" className="text-[var(--brand)] underline font-medium">
                  support@lingoclub.org
                </a>
                , and we will promptly remove the information.
              </p>
            </section>

            {/* Section 7 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">7.</span>
                Your Rights &amp; Choices
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have choices regarding the information you provide and how it is used:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
                <li>
                  <strong className="text-[var(--text)]">Review &amp; Edit Profile:</strong> You can update your account details, photo, bio, languages, and password anytime by logging in and navigating to your Profile Settings.
                </li>
                <li>
                  <strong className="text-[var(--text)]">Account Deletion:</strong> You may request deletion of your account and personal data by sending an email to{" "}
                  <a href="mailto:support@lingoclub.org" className="text-[var(--brand)] underline">
                    support@lingoclub.org
                  </a>
                  .
                </li>
                <li>
                  <strong className="text-[var(--text)]">Communications:</strong> You can opt out of promotional communications by contacting us, although you will continue to receive critical transactional and meeting notices.
                </li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">8.</span>
                Cookies &amp; Local Storage
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and browser local storage to maintain user authentication sessions, remember language preferences, and enhance site navigation. You can adjust your browser settings to refuse cookies, but certain core features of the platform (such as logging in) may not function properly without them.
              </p>
            </section>

            {/* Section 9 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">9.</span>
                Third-Party Links
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our platform may contain links to external websites and services, such as Zoom, VooV, Stripe, or Google Forms. We are not responsible for the privacy practices, content, or terms of third-party platforms. We encourage you to review the privacy policies of any third-party services you visit.
              </p>
            </section>

            {/* Section 10 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">10.</span>
                Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may periodically update this Privacy Policy to reflect changes in our operational practices, technologies, or legal obligations. When changes are made, the revised date at the top of this page will be updated. Your continued use of the platform after any modification constitutes your acceptance of the updated policy.
              </p>
            </section>

            {/* Section 11 */}
            <section className="bg-white border border-[var(--border)] rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--text)] mb-4 flex items-center gap-2">
                <span className="text-[var(--brand)] font-extrabold">11.</span>
                Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, comments, or concerns regarding this Privacy Policy or how your personal information is handled, please contact us:
              </p>
              <div className="bg-[#F8FAFC] border border-[var(--border)] rounded-xl p-5 space-y-2 text-sm text-gray-700">
                <p>
                  <strong className="text-[var(--text)]">Organization:</strong> Lingo Club (501(c)(3) Non-Profit Organization)
                </p>
                <p>
                  <strong className="text-[var(--text)]">EIN:</strong> 99-3850314
                </p>
                <p>
                  <strong className="text-[var(--text)]">Email:</strong>{" "}
                  <a href="mailto:support@lingoclub.org" className="text-[var(--brand)] font-medium underline">
                    support@lingoclub.org
                  </a>
                </p>
                <p>
                  <strong className="text-[var(--text)]">Website:</strong>{" "}
                  <a href="https://lingoclub.org" className="text-[var(--brand)] font-medium underline">
                    https://lingoclub.org
                  </a>
                </p>
              </div>
            </section>

            {/* Cross-link to Terms & Conditions */}
            <div className="bg-gradient-to-r from-[rgba(108,99,255,.08)] to-[rgba(255,107,107,.08)] border border-[var(--border)] rounded-2xl p-6 text-center">
              <p className="text-gray-700 text-sm mb-3">
                Looking for our terms of service and user guidelines?
              </p>
              <Link
                to="/terms-conditions"
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-[var(--brand)] text-white font-semibold text-sm shadow hover:bg-[var(--brand-dark)] transition-all"
              >
                View Terms &amp; Conditions &rarr;
              </Link>
            </div>
          </div>
        </main>
      </div>

      <Footer />
      <MenuBar open={menuOpen} close={closeMenuBar} />
    </div>
  );
};

export default PrivacyPolicy;
